/**
 * Utility functions for automatically applying liquid reflect effects
 * to container elements controlled by the ColorWheelIcon
 */

/**
 * Automatically adds data-liquid-reflect attribute to all container elements
 * that use the bg-container-* classes throughout the app
 */
export function applyLiquidReflectToContainers(): () => void {
  const applyReflect = () => {
    // Target all elements with bg-container classes
    const containerSelectors = [
      '.bg-container-900',
      '.bg-container-800',
      '.bg-container-700',
      '.bg-container-600',
      '.bg-container-800-low'
    ];

    // Find all elements with these classes
    containerSelectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach((element: Element) => {
        // Add data-liquid-reflect if not already present
        if (!element.hasAttribute('data-liquid-reflect')) {
          element.setAttribute('data-liquid-reflect', '');
        }
      });
    });

    // Also target navigation elements that use sidebar-bg-color
    const navElements = document.querySelectorAll('[style*="sidebar-bg-color"], [style*="nav-bg-color"], [style*="ui-bg-color"]');
    navElements.forEach((element: Element) => {
      if (!element.hasAttribute('data-liquid-reflect')) {
        element.setAttribute('data-liquid-reflect', '');
      }
    });
  };

  // Apply immediately
  applyReflect();

  // Set up a mutation observer to apply to dynamically added elements
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node as Element;

          // Check if the added element has container classes
          if (element.classList.contains('bg-container-900') ||
              element.classList.contains('bg-container-800') ||
              element.classList.contains('bg-container-700') ||
              element.classList.contains('bg-container-600') ||
              element.classList.contains('bg-container-800-low')) {
            element.setAttribute('data-liquid-reflect', '');
          }

          // Also check child elements
          const childContainers = element.querySelectorAll('.bg-container-900, .bg-container-800, .bg-container-700, .bg-container-600, .bg-container-800-low');
          childContainers.forEach((child: Element) => {
            if (!child.hasAttribute('data-liquid-reflect')) {
              child.setAttribute('data-liquid-reflect', '');
            }
          });
        }
      });
    });
  });

  // Start observing
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });

  // Return cleanup function
  return () => {
    observer.disconnect();
  };
}

/**
 * Initialize liquid reflect on app startup
 */
export function initLiquidReflect(): void {
  // Apply immediately when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', applyLiquidReflectToContainers);
  } else {
    applyLiquidReflectToContainers();
  }

  // Also apply after navigation changes (for SPAs)
  if (typeof window !== 'undefined') {
    // Listen for navigation events if using a router
    window.addEventListener('popstate', () => {
      setTimeout(applyLiquidReflectToContainers, 100);
    });
  }
}
